import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Trash2, Plus, Users, User as UserIcon } from 'lucide-react'
import {
  aclService,
  groupsService,
  type ACLGrant,
  type ACLRole,
  type OrgGroup,
  type PrincipalType,
} from '../../services/permissions'
import { usersService } from '../../services/users'
import type { ApiErrorLike } from '../../utils/apiError'

interface Props {
  projectId: number
}

const ROLES: ACLRole[] = ['viewer', 'contributor', 'manager', 'owner']

type Candidate =
  | { kind: 'user'; id: number; label: string }
  | { kind: 'group'; id: number; label: string }

/**
 * Project Permissions tab (A6.1).
 *
 * Shows grants on this project + every grant inherited from the project's
 * portfolio/program (read-only on inherited rows). Lets admins add new grants
 * targeting a user or org group.
 */
export function PermissionsSection({ projectId }: Props) {
  const [grants, setGrants] = useState<ACLGrant[]>([])
  const [groups, setGroups] = useState<OrgGroup[]>([])
  const [users, setUsers] = useState<Array<{ id: number; email: string; full_name?: string | null }>>([])
  const [loading, setLoading] = useState(true)

  // Form state for new grant
  const [principalType, setPrincipalType] = useState<PrincipalType>('user')
  const [principalId, setPrincipalId] = useState<number | ''>('')
  const [role, setRole] = useState<ACLRole>('viewer')
  const [saving, setSaving] = useState(false)
  const [forbidden, setForbidden] = useState(false)

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [g, gs, us] = await Promise.all([
        aclService.list({ entity_type: 'project', entity_id: projectId }),
        groupsService.list().catch(() => []),
        usersService.list().catch(() => []),
      ])
      setGrants(g)
      setGroups(gs)
      setUsers(us)
    } catch (err) {
      if ((err as ApiErrorLike)?.response?.status === 403) setForbidden(true)
      else toast.error('Failed to load permissions')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [projectId])

  const handleAdd = async () => {
    if (principalId === '') {
      toast.error(`Select a ${principalType}`)
      return
    }
    setSaving(true)
    try {
      await aclService.upsert({
        principal_type: principalType,
        principal_id: principalId,
        entity_type: 'project',
        entity_id: projectId,
        role,
      })
      toast.success('Grant saved')
      setPrincipalId('')
      await fetchAll()
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(err?.response?.data?.detail || 'Failed to save grant')
    } finally {
      setSaving(false)
    }
  }

  const handleRevoke = async (grantId: number) => {
    if (!confirm('Revoke this grant?')) return
    try {
      await aclService.revoke(grantId)
      toast.success('Grant revoked')
      await fetchAll()
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(err?.response?.data?.detail || 'Failed to revoke grant')
    }
  }

  const principalChoices: Candidate[] =
    principalType === 'user'
      ? users.map((u) => ({ kind: 'user', id: u.id, label: u.full_name || u.email }))
      : groups.map((g) => ({ kind: 'group', id: g.id, label: g.name }))

  return (
    <div className="p-4 sm:p-6 max-w-4xl space-y-6">
      <header>
        <h2 className="text-lg font-semibold text-[#102a43]">Project permissions</h2>
        <p className="text-sm text-[#486581] mt-1">
          Grant access to this project. Grants made on the parent portfolio or program
          roll down automatically.
        </p>
      </header>

      {forbidden && (
        <p role="status" className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          Only workspace admins can see and change who has access to this project.
        </p>
      )}

      {!forbidden && <>
      {/* New grant form */}
      <section className="bg-white rounded-lg border-2 border-[#bcccdc] p-4 space-y-3">
        <h3 className="text-sm font-semibold text-[#102a43]">Add grant</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <select
            className="border border-[#bcccdc] rounded px-2 py-1.5 text-sm"
            value={principalType}
            onChange={(e) => {
              setPrincipalType(e.target.value as PrincipalType)
              setPrincipalId('')
            }}
          >
            <option value="user">User</option>
            <option value="group">Group</option>
          </select>
          <select
            className="border border-[#bcccdc] rounded px-2 py-1.5 text-sm sm:col-span-2"
            value={principalId === '' ? '' : String(principalId)}
            onChange={(e) =>
              setPrincipalId(e.target.value === '' ? '' : parseInt(e.target.value, 10))
            }
          >
            <option value="">Choose {principalType}…</option>
            {principalChoices.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
          <select
            className="border border-[#bcccdc] rounded px-2 py-1.5 text-sm"
            value={role}
            onChange={(e) => setRole(e.target.value as ACLRole)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={saving || principalId === ''}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-[#102a43] text-white disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> Grant access
        </button>
      </section>

      {/* Existing grants */}
      <section className="bg-white rounded-lg border-2 border-[#bcccdc]">
        <header className="px-4 py-2 border-b border-[#bcccdc]">
          <h3 className="text-sm font-semibold text-[#102a43]">Current grants</h3>
        </header>
        {loading ? (
          <p className="p-4 text-sm text-[#627d98]">Loading…</p>
        ) : grants.length === 0 ? (
          <p className="p-4 text-sm text-[#627d98]">
            No grants on this project yet. Anyone with platform-wide project access
            still applies.
          </p>
        ) : (
          <ul className="divide-y divide-[#bcccdc]">
            {grants.map((g) => (
              <li key={g.id} className="flex items-center justify-between px-4 py-2 text-sm">
                <div className="flex items-center gap-2">
                  {g.principal_type === 'group' ? (
                    <Users className="w-4 h-4 text-[#486581]" />
                  ) : (
                    <UserIcon className="w-4 h-4 text-[#486581]" />
                  )}
                  <span className="font-medium text-[#102a43]">
                    {g.principal_label || `${g.principal_type} #${g.principal_id}`}
                  </span>
                  <span className="text-xs text-[#627d98] uppercase">{g.role}</span>
                </div>
                <button
                  type="button"
                  className="text-[#9b2c2c] hover:underline inline-flex items-center gap-1"
                  onClick={() => handleRevoke(g.id)}
                >
                  <Trash2 className="w-4 h-4" /> Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      </>}
    </div>
  )
}
